import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { salesService } from "./sale.service";

const initialState = {
  sales: [],
  drafts: [],
  returns: [],
  currentSale: null,
  pagination: {},
  returnsPagination: {},
  returnsSummary: null,
  summary: null,
  loading: false,
  draftsLoading: false,
  returnsLoading: false,
  error: null,
  success: false,
  message: "",
};

export const fetchSales = createAsyncThunk(
  "sales/fetchSales",
  async (params, { rejectWithValue }) => {
    try {
      const response = await salesService.getAllSales(params);
      return response; // Return full response { success, data, pagination, summary }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch sales");
    }
  }
);

export const fetchSaleDetails = createAsyncThunk(
  "sales/fetchSaleDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await salesService.getSaleDetails(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch sale details");
    }
  }
);

export const addSale = createAsyncThunk(
  "sales/addSale",
  async (saleData, { rejectWithValue }) => {
    try {
      const response = await salesService.addSale(saleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add sale");
    }
  }
);

export const processReturn = createAsyncThunk(
  "sales/processReturn",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesService.processReturn(id, data);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to process return");
    }
  }
);

export const addPayment = createAsyncThunk(
  "sales/addPayment",
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await salesService.addPayment(paymentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add payment");
    }
  }
);

export const generateInvoice = createAsyncThunk(
  "sales/generateInvoice",
  async (saleId, { rejectWithValue }) => {
    try {
      const blob = await salesService.generateInvoice(saleId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${saleId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return saleId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to generate invoice");
    }
  }
);

export const generateReceipt = createAsyncThunk(
  "sales/generateReceipt",
  async (saleId, { rejectWithValue }) => {
    try {
      const blob = await salesService.generateReceipt(saleId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${saleId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return saleId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to generate receipt");
    }
  }
);

// Draft Thunks
export const fetchDrafts = createAsyncThunk(
  "sales/fetchDrafts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await salesService.getDrafts(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch drafts");
    }
  }
);

export const createDraft = createAsyncThunk(
  "sales/createDraft",
  async (draftData, { rejectWithValue }) => {
    try {
      const response = await salesService.createDraft(draftData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to save draft");
    }
  }
);

export const updateDraft = createAsyncThunk(
  "sales/updateDraft",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesService.updateDraft(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update draft");
    }
  }
);

export const convertDraftToSale = createAsyncThunk(
  "sales/convertDraftToSale",
  async ({ id, paymentData }, { rejectWithValue }) => {
    try {
      const response = await salesService.convertDraftToSale(id, paymentData);
      return { id, sale: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to convert draft");
    }
  }
);

export const deleteDraft = createAsyncThunk(
  "sales/deleteDraft",
  async (id, { rejectWithValue }) => {
    try {
      await salesService.deleteDraft(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete draft");
    }
  }
);

// Return History Thunks
export const fetchReturnHistory = createAsyncThunk(
  "sales/fetchReturnHistory",
  async (params, { rejectWithValue }) => {
    try {
      const response = await salesService.getReturnHistory(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch return history");
    }
  }
);

const salesSlice = createSlice({
  name: "sales",
  initialState,
  reducers: {
    reset: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = "";
    },
    clearCurrentSale: (state) => {
      state.currentSale = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Sales
      .addCase(fetchSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSales.fulfilled, (state, action) => {
        state.loading = false;
        state.sales = action.payload?.data || action.payload || [];
        state.pagination = action.payload?.pagination || {};
        state.summary = action.payload?.summary || null;
        state.error = null;
      })
      .addCase(fetchSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.sales = [];
        state.pagination = {};
      })

      // Fetch Sale Details
      .addCase(fetchSaleDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSaleDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSale = action.payload;
        state.error = null;
      })
      .addCase(fetchSaleDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentSale = null;
      })

      // Add Sale
      .addCase(addSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addSale.fulfilled, (state, action) => {
        state.loading = false;
        state.sales.unshift(action.payload);
        state.success = true;
        state.message = "Sale completed successfully";
        state.error = null;
      })
      .addCase(addSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Process Return
      .addCase(processReturn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processReturn.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.isPartialReturn
          ? "Partial return processed successfully"
          : "Return processed successfully";
        // Update the sale status in the list
        const index = state.sales.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sales[index].status = action.payload.newStatus || 'returned';
        }
        state.error = null;
      })
      .addCase(processReturn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Add Payment
      .addCase(addPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Payment added successfully";
        state.error = null;
      })
      .addCase(addPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Generate Invoice
      .addCase(generateInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateInvoice.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.message = "Invoice generated successfully";
        state.error = null;
      })
      .addCase(generateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Generate Receipt
      .addCase(generateReceipt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateReceipt.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.message = "Receipt generated successfully";
        state.error = null;
      })
      .addCase(generateReceipt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Fetch Drafts
      .addCase(fetchDrafts.pending, (state) => {
        state.draftsLoading = true;
        state.error = null;
      })
      .addCase(fetchDrafts.fulfilled, (state, action) => {
        state.draftsLoading = false;
        state.drafts = action.payload || [];
        state.error = null;
      })
      .addCase(fetchDrafts.rejected, (state, action) => {
        state.draftsLoading = false;
        state.error = action.payload;
        state.drafts = [];
      })

      // Create Draft
      .addCase(createDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDraft.fulfilled, (state, action) => {
        state.loading = false;
        state.drafts.unshift(action.payload);
        state.success = true;
        state.message = "Draft saved successfully";
        state.error = null;
      })
      .addCase(createDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Update Draft
      .addCase(updateDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDraft.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.drafts.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.drafts[index] = action.payload;
        }
        state.success = true;
        state.message = "Draft updated successfully";
        state.error = null;
      })
      .addCase(updateDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Convert Draft to Sale
      .addCase(convertDraftToSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(convertDraftToSale.fulfilled, (state, action) => {
        state.loading = false;
        state.drafts = state.drafts.filter(d => d.id !== action.payload.id);
        state.sales.unshift(action.payload.sale);
        state.success = true;
        state.message = "Draft converted to sale successfully";
        state.error = null;
      })
      .addCase(convertDraftToSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Delete Draft
      .addCase(deleteDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDraft.fulfilled, (state, action) => {
        state.loading = false;
        state.drafts = state.drafts.filter(d => d.id !== action.payload);
        state.success = true;
        state.message = "Draft deleted successfully";
        state.error = null;
      })
      .addCase(deleteDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Fetch Return History
      .addCase(fetchReturnHistory.pending, (state) => {
        state.returnsLoading = true;
        state.error = null;
      })
      .addCase(fetchReturnHistory.fulfilled, (state, action) => {
        state.returnsLoading = false;
        state.returns = action.payload?.data || [];
        state.returnsPagination = action.payload?.pagination || {};
        state.returnsSummary = action.payload?.summary || null;
        state.error = null;
      })
      .addCase(fetchReturnHistory.rejected, (state, action) => {
        state.returnsLoading = false;
        state.error = action.payload;
        state.returns = [];
      });
  },
});

export const { reset, clearCurrentSale } = salesSlice.actions;
export default salesSlice.reducer;
